import { useCallback, useState } from "react";
import { iconSizes } from "@stubhub/broadway-types";
import {
  ButtonKind,
  ButtonLink,
  ButtonVariant,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalMobileVariant,
  Text,
} from "@stubhub/broadway-web";
import { StarFillIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsAndroidApp, useIsStubhub } from "src/shared/utils/appContextHelper";
import { APP_STORE_LINKS, COBRAND_IDENTIFIERS } from "src/website-apps/shared/constants/app-install";
import { getAppInstallQuickLinkUrl } from "src/website-apps/shared/utils/getAppInstallQuickLinkUrl";
import {
  appIconStyle,
  bodyStyle,
  ctaArrowStyle,
  ctaButtonStyle,
  ctaFooterStyle,
  headingStyle,
  leftCardStyle,
  modalCardClass,
  ratingGroupStyle,
  ratingScoreStyle,
  ratingStarsStyle,
  ratingsCountStyle,
  ratingsLabelStyle,
  rightCardStyle,
} from "./AppInstallInterstitialMobile.styles";
import { ArrowRightIcon } from "./ArrowRightIcon";
import type { AppInstallInterstitialPlacement } from "./types";
import { useAppInstallInterstitialStorage } from "./useAppInstallInterstitialStorage";
import { placementToLinkType } from "./utils";

const STAR_COUNT = 5;

// ─── Props ───────────────────────────────────────────────────────────────────

interface AppInstallInterstitialMobileProps {
  placement: AppInstallInterstitialPlacement;
}

/**
 * App install interstitial — Mobile variant (bottom sheet).
 *
 * Three-element rating group (left card -15deg, center icon, right card +15deg),
 * headline text, subtitle, and a single "Get the App" CTA button.
 * Uses an Adjust smart link that redirects to the correct store per platform.
 *
 * Manages its own show/hide storage state — callers (e.g. AddFeature) only need
 * to mount this component unconditionally for variant users.
 *
 * Figma: https://www.figma.com/design/TBcCjPU38IObny3cJOFDQ5/App-Downloads?node-id=586-13715
 * JIRA:  https://thestubhub.atlassian.net/browse/CXWD-3699
 */
export const AppInstallInterstitialMobile = ({ placement }: AppInstallInterstitialMobileProps) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const { shouldShow, markDismissed, markConverted } = useAppInstallInterstitialStorage();
  const [isOpen, setIsOpen] = useState(true);
  const isSh = useIsStubhub();
  const isAndroid = useIsAndroidApp();
  const cobrandName = isSh ? COBRAND_IDENTIFIERS.SH : COBRAND_IDENTIFIERS.VGG;
  const linkType = placementToLinkType(placement);
  // Use REACT_APP_ASSETS_URL so the correct CDN host is used per environment
  // Mobile uses the non-rotated icon (rotation is in the left/right cards instead)
  const appIconSrc = `${appContext.env["REACT_APP_ASSETS_URL"]}/images/Assets/Icons/bfx/stubhub-logo-app-install-interstitial.svg`;

  // cid$#3376228# — "Download the {cobrandName} app"
  const titleContent = useFormattedContent("cid$#3376228#", [cobrandName]);
  // cid$#4248105# — "Buy, sell, and manage your tickets with ease"
  const subtitleContent = useContent("cid$#4248105#");
  // cid$#4250581# — "1.4m+" (abbreviated rating count)
  const ratingsCountContent = useContent("cid$#4250581#");
  // cid$#4250582# — "Ratings"
  const ratingsLabelContent = useContent("cid$#4250582#");
  // cid$#4253418# — "Get the App"
  const ctaContent = useContent("cid$#4253418#");

  // Adjust smart link — platform-agnostic, redirects to App Store (iOS) or
  // Google Play (Android) on the device side. Falls back to platform-specific store URL.
  const appUrl =
    getAppInstallQuickLinkUrl({ userId: appContext.userId, appContext, type: linkType, isStubhub: isSh }) ??
    APP_STORE_LINKS?.[cobrandName]?.[isAndroid ? "GOOGLE" : "APPLE"];

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        return;
      }

      setIsOpen(false);
      storePageEvent(PE_TYPE.CLOSE, PE_KEYS.APP_INSTALL_INTERSTITIAL_DISMISS, "");
      markDismissed();
    },
    [markDismissed, storePageEvent],
  );

  const handleCtaClick = useCallback(() => {
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.APP_INSTALL_INTERSTITIAL_CTA_CLICK, `${cobrandName}_${placement}`);
    markConverted();
  }, [cobrandName, placement, markConverted, storePageEvent]);

  if (!shouldShow) return null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      dismissible
      mobileVariant={ModalMobileVariant.BottomSheet}
      classes={{ content: modalCardClass }}
    >
      <ModalHeader />
      <ModalBody>
        <div className={bodyStyle}>
          {/* Three-element rating group */}
          <div className={ratingGroupStyle}>
            {/* Left: 4.8 + stars, rotated -15deg */}
            <div className={leftCardStyle}>
              <span className={ratingScoreStyle} aria-label="4.8 stars">
                4.8
              </span>
              <div className={ratingStarsStyle}>
                {Array.from({ length: STAR_COUNT }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-length list
                  <StarFillIcon key={i} size={iconSizes[0]} />
                ))}
              </div>
            </div>
            {/* Right: rating count + label, rotated +15deg */}
            <div className={rightCardStyle}>
              <span className={ratingsCountStyle}>{ratingsCountContent}</span>
              <span className={ratingsLabelStyle}>{ratingsLabelContent}</span>
            </div>
            {/* Center: app icon 100×100, z-index above both cards */}
            <img src={appIconSrc} alt="" aria-hidden="true" className={appIconStyle} />
          </div>

          {/* Headline — larger gap after rating group */}
          <Heading variant="header" className={headingStyle}>
            {titleContent}
          </Heading>

          {/* Subtitle */}
          <Text>{subtitleContent}</Text>
        </div>
      </ModalBody>
      <ModalFooter className={ctaFooterStyle}>
        <ButtonLink
          kind={ButtonKind.Neutral}
          variant={ButtonVariant.Regular}
          classes={{ root: ctaButtonStyle }}
          href={appUrl}
          onClick={handleCtaClick}
        >
          {ctaContent}{" "}
          <span className={ctaArrowStyle}>
            <ArrowRightIcon />
          </span>
        </ButtonLink>
      </ModalFooter>
    </Modal>
  );
};
